import React, { useEffect, useState, useCallback } from 'react';
import _ from 'lodash';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import moment from 'moment';
import { toast } from 'react-toastify';
import classes from './tabs.module.scss';
import Api from '../../../../../api/api';
import accountHelpers from '../../../../../utils/accountHelpers';
import { getScriptsRequest } from '../../../../../store/actions/script';
import { getPhysicianScriptRequest } from '../../../../../store/actions/physicians';
import { useQuery } from '../../../../../utils/hooks/useQuery';
import Loader from '../../../../_common/Loader/Loader';
import Pagination from '../../../../_common/Pagination/Pagination';
import Button from '../../../../_common/Form/Buttons/Button';
import Table from '../../../../_common/Tables/Table';
import ScriptsStatusBar from '../../../Scripts/ScriptsStatusBar/ScriptsStatusBar';

const tableHeader = [
  {
    name: 'Status',
    path: 'status',
  },
  {
    name: 'Date',
    path: 'processedOn',
    render: (s) => (moment(s.processedOn).isValid()
      ? moment(s.processedOn).format('MM/DD/YYYY')
      : '-'),
  },
  {
    name: 'Patient',
    path: 'Patient',
    render: (s) => `${s.Patient?.firstName} ${s.Patient?.lastName} `,
  },
  {
    name: 'Medication',
    path: 'Product.name',
    render: (s) => _.get(s, 'Product.name') || '-',
  },
  {
    name: 'Other',
    path: 'other',
  },
];

const Script = ({ type }) => {
  const { physicianId, groupName } = useParams();
  const { query } = useQuery();

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [btnLoading, setBtnLoading] = useState(false);
  const { scriptData, scriptTotalPage, requestStatus } = useSelector((store) => store.script);
  const { physicianScript } = useSelector((store) => store.physicians);

  const initialData = useCallback(() => {
    const data = {
      userId: accountHelpers.getUser().id,
      limit: 15,
      page: 1,
    };

    if (type === 'health') {
      data.healthId = query?.hubId;
    } else {
      data.physicianId = physicianId;
    }

    return data;
  }, []);

  const [requestData, setRequestData] = useState(initialData);
  const [loading, setLoading] = useState(false);
  const [load, setLoad] = useState(true);
  const [activeStatuses, setActiveStatuses] = useState([]);

  const { limit, page } = requestData;

  useEffect(() => {
    (async () => {
      setLoad(true);

      if (type === 'group') {
        await dispatch(getPhysicianScriptRequest({ physGroup: groupName, userId: accountHelpers.getUser().id }));
      } else {
        await dispatch(getScriptsRequest(requestData));
      }
      setLoad(false);
    })();
  }, [type]);

  const scripts = _.orderBy(
    physicianScript.map((p) => p.Scripts).flat(1),
    (s) => (moment(s.processedOn).isValid() ? moment(s.processedOn).valueOf() : -1),
    'desc',
  );

  const onChangePageData = async (path, count) => {
    setLoading(true);

    const newRequestData = { ...requestData, [path]: count };

    if (path === 'limit') {
      newRequestData.page = 1;
    }

    await dispatch(getScriptsRequest({ ...newRequestData, status: activeStatuses.join(',') }));

    setRequestData(newRequestData);

    setLoading(false);
  };

  const onExportClick = async () => {
    setBtnLoading(true);
    try {
      await Api.getScripts({ ...requestData, status: activeStatuses.join(','), download: true });
      toast.success('The file will be generated in background and will be sent you via email');
    } catch (e) {
      toast.error('Something wrong try again');
    }
    setBtnLoading(false);
  };

  const changeStatus = async (name) => {
    const status = activeStatuses.includes(name)
      ? activeStatuses.filter((s) => s !== name)
      : [...activeStatuses, name];

    setActiveStatuses(status);

    if (type === 'group') return;

    setLoading(true);

    const newRequestData = { ...requestData, page: 1 };

    await dispatch(getScriptsRequest({ ...newRequestData, status: status.join(',') }));

    setRequestData(newRequestData);

    setLoading(false);
  };

  const physicianScripts = type === 'group'
    ? (activeStatuses.length ? scripts.filter((s) => activeStatuses.includes(s.status)) : scripts)
    : scriptData;

  return (
    (!loading && requestStatus === 'request') || load
      ? <Loader className={classes.loader} />

      : (
        <>
          {accountHelpers.getUser().role === 'Health' && (
            <Button
              title="Export"
              onClick={onExportClick}
              loading={btnLoading}
            />
          )}

          <ScriptsStatusBar
            activeStatuses={activeStatuses}
            onClick={changeStatus}
          />

          <div style={{marginTop: 20}}>
            <Table
              data={physicianScripts}
              header={tableHeader}
              onClick={(id) => navigate(`/scripts/${id}`)}
              loading={loading}
            />
          </div>

          {type !== 'group' && (
            <Pagination
              page={+page - 1}
              limit={limit}
              totalPage={+scriptTotalPage}
              onChange={onChangePageData}
              showLimit
            />
          )}
        </>
      )

  );
};

Script.propTypes = {
  type: PropTypes.string,
};

Script.defaultProps = {
  type: '',
};

export default Script;
